import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Users, Shield, CreditCard, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UserData {
  id: string;
  username: string | null;
  email: string;
  is_admin: boolean;
  credits_balance: number;
}

interface BulkUserActionsProps {
  users: UserData[];
  selectedUsers: string[];
  onSelectionChange: (userIds: string[]) => void;
  onRefresh: () => void;
}

export default function BulkUserActions({ users, selectedUsers, onSelectionChange, onRefresh }: BulkUserActionsProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [bulkAction, setBulkAction] = useState<string>("");

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(users.map(user => user.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.length === 0) return;

    setIsProcessing(true);
    try {
      switch (bulkAction) {
        case "make_admin":
          await Promise.all(
            selectedUsers.map(userId => 
              supabase.rpc('toggle_admin_role', { 
                target_user_id: userId, 
                make_admin: true 
              })
            )
          );
          toast.success(`Made ${selectedUsers.length} users admin`);
          break;

        case "remove_admin":
          await Promise.all(
            selectedUsers.map(userId => 
              supabase.rpc('toggle_admin_role', { 
                target_user_id: userId, 
                make_admin: false 
              })
            )
          );
          toast.success(`Removed admin role from ${selectedUsers.length} users`);
          break;

        case "add_credits":
          await Promise.all(
            selectedUsers.map(userId => 
              supabase.rpc('manage_user_credits', { 
                target_user_id: userId, 
                amount: 10,
                description: 'Bulk credit addition by admin'
              })
            )
          );
          toast.success(`Added 10 credits to ${selectedUsers.length} users`);
          break;

        case "reset_credits":
          await Promise.all(
            selectedUsers.map(userId => 
              supabase.rpc('reset_user_credit_usage', { 
                target_user_id: userId 
              })
            )
          );
          toast.success(`Reset credits for ${selectedUsers.length} users`);
          break;

        case "delete_users":
          await Promise.all(
            selectedUsers.map(userId => 
              supabase.rpc('admin_delete_user', { 
                target_user_id: userId 
              })
            )
          );
          toast.success(`Permanently deleted ${selectedUsers.length} users and all their data`);
          break;
      }

      onRefresh();
      onSelectionChange([]);
      setBulkAction("");
    } catch (error: any) {
      console.error('Bulk action error:', error);
      toast.error(`Failed to perform bulk action: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const allSelected = users.length > 0 && selectedUsers.length === users.length;
  const someSelected = selectedUsers.length > 0 && selectedUsers.length < users.length;

  return (
    <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg border">
      <div className="flex items-center space-x-2">
        <Checkbox
          checked={allSelected}
          onCheckedChange={handleSelectAll}
        />
        <span className="text-sm font-medium">
          {selectedUsers.length > 0 
            ? `${selectedUsers.length} selected` 
            : "Select all"
          }
        </span>
      </div>

      {selectedUsers.length > 0 && (
        <>
          <Select value={bulkAction} onValueChange={setBulkAction}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="make_admin">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Make Admin
                </div>
              </SelectItem>
              <SelectItem value="remove_admin">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Remove Admin
                </div>
              </SelectItem>
              <SelectItem value="add_credits">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Add 10 Credits
                </div>
              </SelectItem>
              <SelectItem value="reset_credits">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Reset Credits
                </div>
              </SelectItem>
              <SelectItem value="delete_users" className="text-destructive focus:text-destructive">
                <div className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  Delete Users
                </div>
              </SelectItem>
            </SelectContent>
          </Select>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant={bulkAction === "delete_users" ? "destructive" : "outline"}
                disabled={!bulkAction || isProcessing}
                className="flex items-center gap-2"
              >
                {isProcessing ? "Processing..." : "Apply"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className={bulkAction === "delete_users" ? "text-destructive" : ""}>
                  Confirm Bulk Action
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {bulkAction === "delete_users" ? (
                    <div className="space-y-3">
                      <p className="font-medium text-destructive">
                        Permanently delete {selectedUsers.length} user account(s)?
                      </p>
                      <div className="p-3 bg-destructive/10 rounded-md border border-destructive/20">
                        <p className="font-medium text-sm mb-2">Users to be deleted:</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {users.filter(user => selectedUsers.includes(user.id)).map(user => (
                            <div key={user.id} className="text-sm">
                              {user.email} {user.username && `(${user.username})`}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="p-3 bg-destructive/10 rounded-md border border-destructive/20">
                        <p className="font-semibold text-destructive text-sm">⚠️ This will permanently delete:</p>
                        <ul className="text-xs mt-1 space-y-1 text-muted-foreground">
                          <li>• All user profiles and account data</li>
                          <li>• Credit transactions and usage history</li>
                          <li>• Subscription information</li>
                          <li>• Generated ideas and saved content</li>
                          <li>• Token usage records</li>
                          <li>• All other associated user data</li>
                        </ul>
                      </div>
                      <p className="text-sm font-medium text-destructive">
                        This action cannot be undone.
                      </p>
                    </div>
                  ) : (
                    <>
                      Are you sure you want to apply this action to {selectedUsers.length} selected users? 
                      This action cannot be undone.
                    </>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleBulkAction}
                  className={bulkAction === "delete_users" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
                >
                  Confirm
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => onSelectionChange([])}
          >
            Clear Selection
          </Button>
        </>
      )}
    </div>
  );
}